// Dashboard.js — Overview (smart, focused) + Bonus.
// Charts module merged INTO Overview (2026-02). Legacy 59-program logic removed.
//
// v200.51 (2026-08-14) — «الربح التقديري» and «الهامش» no longer read
// hard-coded COGS/FIX constants. The paid-COGS ratio is fetched from
// `GET /api/analytics/cogs-window` for the panel's actual date range,
// AND the free-goods cost is displayed as a SEPARATE KPI (rule (b):
// paid margin ≠ campaign cost — mixing them yields a number that is
// neither). Fixed monthly costs (previously 47,500) have no data source
// and are shown as «غير موثّق» until the owner registers them in a
// dedicated collection. Guarded by
// `backend/tests/test_cogs_dashboard_wiring.py`.
import React from "react";
import { fm, byDay, byWeek } from "./lib/analytics";
import { mkC } from "./lib/chartFactory";
import { api, formatErr } from "./lib/api";
import GoalsBar from "./GoalsBar";
import HealthKpiBar from "./HealthKpiBar";
import { ksaAddDays, ksaDayOfWeek, ksaDaysInMonth, ksaParts, ksaStartOfMonth, ksaStartOfWeek, ksaToday } from "./lib/ksaTime";

// Kpi card grid renderer (also used by SalesPage)
export const Kpi = ({ items }) => (
  <div className="kg">
    {items.map((k) => (
      <div key={`${k.l}-${k.v}`} className={`kc ${k.c || ""}`} data-testid={k.testId}>
        <div className="kl">{k.l}</div>
        <div className={`kv ${k.c || ""}`}>{k.v}</div>
        {k.badge && (
          <div data-testid="kpi-official-badge"
               style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 800,
                        color: "#065F46", background: "rgba(6,95,70,0.10)",
                        padding: "2px 8px", borderRadius: 999 }}>
            {k.badge}
          </div>
        )}
        {k.s && <div className="ks">{k.s}</div>}
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════
// OVERVIEW — smart, focused dashboard with merged charts
// ═══════════════════════════════════════════════════════════════════════
export function Overview({ fil, cst, dsum, period, dateFrom, dateTo, hideHealth = false }) {
  const ds = React.useMemo(() => byDay(fil || []), [fil]);
  const wks = React.useMemo(() => byWeek(ds), [ds]);

  // AI reconciliation state (used only when dsum is uploaded)
  const [aiText, setAiText] = React.useState("");
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiErr, setAiErr] = React.useState(null);
  const runAi = async () => {
    setAiBusy(true); setAiErr(null);
    try {
      const { data } = await api.post("/ai/reconcile-insights");
      setAiText(data.text || "");
    } catch (e) {
      setAiErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setAiBusy(false); }
  };

  // Filter dsum (POS authoritative) by the same period as fil
  const dsumFiltered = React.useMemo(() => {
    if (!dsum || !dsum.length) return [];
    // Local-time formatter — avoids UTC shift bug
    // ⑤/L3-أ — بتوقيت السعودية عبر lib/ksaTime.js، وبداية الأسبوع إعداد.
    const td = ksaToday();
    const yd = ksaAddDays(td, -1);
    const wk = ksaAddDays(td, -7);
    const wk30 = ksaAddDays(td, -30);
    const ms = ksaStartOfMonth(td);
    const prevMonthEnd = ksaAddDays(ms, -1);
    const prevMonthStart = ksaStartOfMonth(prevMonthEnd);
    const wkStart = ksaStartOfWeek(td);
    const prevWkEnd = ksaAddDays(wkStart, -1);
    const prevWkStart = ksaAddDays(wkStart, -7);
    let fr = "", to = td;
    if (period === "today") { fr = td; to = td; }
    else if (period === "yesterday") { fr = yd; to = yd; }
    else if (period === "week") fr = wk;
    else if (period === "week30") fr = wk30;
    else if (period === "thisweek") fr = wkStart;
    else if (period === "prevweek") { fr = prevWkStart; to = prevWkEnd; }
    else if (period === "month") fr = ms;
    else if (period === "prevmonth") { fr = prevMonthStart; to = prevMonthEnd; }
    else if (period === "custom") { fr = dateFrom || ""; to = dateTo || td; }
    return dsum.filter((r) => (!fr || r.date >= fr) && (!to || r.date <= to));
  }, [dsum, period, dateFrom, dateTo]);
  const hasDsum = dsumFiltered.length > 0;

  // POS aggregates
  const dsumAgg = React.useMemo(() => {
    if (!hasDsum) return null;
    const sum = (k) => dsumFiltered.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    const cnt = sum("count");
    const net_with_tax = sum("net_with_tax");
    const net = sum("net");
    const gross = sum("gross");
    const disc = sum("disc");
    const tax = sum("tax");
    const profit = sum("profit");
    const customers = sum("customers");
    const return_value = sum("return_value");
    const return_count = sum("return_count");
    const cancel_value = sum("cancel_value");
    const cancel_count = sum("cancel_count");
    const fees = sum("fees");
    const avg = cnt > 0 ? net_with_tax / cnt : 0;
    return { cnt, net_with_tax, net, gross, disc, tax, profit, customers, return_value, return_count, cancel_value, cancel_count, fees, avg, days: dsumFiltered.length };
  }, [dsumFiltered, hasDsum]);

  // Headline metrics from invoices
  const tr = ds.reduce((s, d) => s + d.total, 0);     // paid
  const tdisc = ds.reduce((s, d) => s + d.disc, 0);   // discount
  const gross = tr + tdisc;
  const tc = ds.reduce((s, d) => s + d.count, 0);
  const tp = ds.reduce((s, d) => s + (d.paid || 0), 0);   // paid invoices
  const days = ds.length;

  // v200.51 (2026-08-14) — COGS/FIX no longer hard-coded. We fetch the
  // paid-COGS ratio for THIS panel's date range from
  // `/api/analytics/cogs-window`. Free-goods cost is returned as a
  // SEPARATE number and rendered as its own KPI (owner rule (b): the
  // paid margin is not the campaign cost — mixing them produces
  // neither). Coverage by revenue AND by quantity is always rendered
  // (owner rule (c)).
  const [cogsWin, setCogsWin] = React.useState(null);   // full response
  const [cogsErr, setCogsErr] = React.useState(null);
  React.useEffect(() => {
    if (!ds.length) return;
    const first = ds[0].date;
    const last  = ds[ds.length - 1].date;
    let alive = true;
    setCogsErr(null);
    api.get("/analytics/cogs-window", { params: { from: first, to: last } })
      .then((r) => { if (alive) setCogsWin(r.data || null); })
      .catch((e) => {
        if (!alive) return;
        setCogsErr(formatErr(e.response?.data?.detail) || e.message || "خطأ");
        setCogsWin(null);
      });
    return () => { alive = false; };
  }, [ds]);

  // Paid COGS ratio — derived per period. `null` = not computed yet or
  // no priced paid lines in the window. NEVER default to a constant.
  const paidCogsPct  = cogsWin?.paid?.cogs_pct ?? null;              // 0..1
  const freeGoodsCost = cogsWin?.free?.priced_cost ?? null;          // ر
  const covByRev = cogsWin?.coverage?.by_revenue ?? null;            // 0..1
  const covByQty = cogsWin?.coverage?.by_quantity ?? null;           // 0..1
  const fixedCostsMeta = cogsWin?.fixed_costs ?? null;               // {status,...}
  const hasCogs = paidCogsPct !== null && paidCogsPct > 0;

  // Paid profit BEFORE fixed costs (rule (b): keep it separate from
  // free-goods cost; keep it separate from unsourced fixed costs).
  const pf = hasCogs ? tr * (1 - paidCogsPct) : null;
  const mg = (hasCogs && tr > 0) ? (pf / tr) * 100 : null;
  const ar = days > 0 ? tr / days : 0;
  const dp = gross > 0 ? (tdisc / gross) * 100 : 0;
  const aov = tp > 0 ? tr / tp : 0;
  const paidRate = tc > 0 ? (tp / tc) * 100 : 0;
  const best = ds.length ? [...ds].sort((a, b) => b.total - a.total)[0] : null;
  const worst = ds.length ? [...ds].sort((a, b) => a.total - b.total)[0] : null;

  // Smart alerts
  const alerts = React.useMemo(() => {
    if (ds.length < 2) return [];
    const today = ds[ds.length - 1];
    const prevAvg = ds.slice(0, -1).reduce((s, d) => s + d.total, 0) / (ds.length - 1);
    const out = [];
    if (today.total > prevAvg * 1.15) out.push({ t: "g", m: `▲ آخر يوم متفوّق: ${fm(today.total)} ر — أعلى من المتوسط بـ ${fm(Math.round(today.total - prevAvg))} ر` });
    else if (today.total < prevAvg * 0.85) out.push({ t: "r", m: `▼ آخر يوم منخفض: ${fm(today.total)} ر — أقل من المتوسط بـ ${fm(Math.round(prevAvg - today.total))} ر` });
    if (dp > 65) out.push({ t: "o", m: `⚠ نسبة الخصم ${dp.toFixed(1)}% — أعلى من المعقول (60%). راجع تكلفة العروض.` });
    if (mg !== null && mg < 5 && days > 7) out.push({ t: "r", m: `⚠ الهامش ${mg.toFixed(1)}% — تحت العتبة الصحية. راجع التكاليف الثابتة.` });
    if (paidRate < 70) out.push({ t: "o", m: `📋 نسبة الفواتير المدفوعة ${paidRate.toFixed(0)}% — راجع كمية الفواتير المجانية.` });
    return out;
  }, [ds, dp, mg, paidRate, days]);

  // Last 14 days table (cleaner, no 59 column)
  const last14 = React.useMemo(() => [...ds].reverse().slice(0, 14), [ds]);

  // Month-to-date for GoalsBar
  const { monthRevenue, monthCount, monthAvgInvoice, monthRegistration, hasInvoicePhones } = React.useMemo(() => {
    const now = new Date();
    const ymPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const mr = (fil || []).filter((r) => (r.date || "").startsWith(ymPrefix));
    const rev = mr.reduce((a, r) => a + (Number(r.total) || 0) + (Number(r.disc) || 0), 0);
    const cnt = mr.length;
    const avgInv = cnt > 0 ? rev / cnt : 0;
    // Registration via phone/name match with customer file
    const np = (raw) => {
      if (!raw) return "";
      const d = String(raw).replace(/\D/g, "");
      if (!d) return "";
      let x = d;
      if (x.startsWith("00966")) x = x.slice(5);
      else if (x.startsWith("966")) x = x.slice(3);
      if (x.startsWith("0")) x = x.slice(1);
      return x;
    };
    const nn = (raw) => String(raw || "").trim().toLowerCase();
    const phones = new Set(); const names = new Set();
    (cst || []).forEach((c) => {
      const p = np(c.phone); if (p) phones.add(p);
      const n = nn(c.name); if (n && n !== "nan" && n !== "—") names.add(n);
    });
    const registered = (phones.size + names.size) > 0
      ? mr.filter((r) => {
          const p = np(r.phone); if (p && phones.has(p)) return true;
          if (!p) { const n = nn(r.client); if (n && names.has(n)) return true; }
          return false;
        }).length
      : 0;
    const regRate = cnt > 0 ? Math.min(100, Math.max(0, registered / cnt * 100)) : 0;
    return { monthRevenue: rev, monthCount: cnt, monthAvgInvoice: avgInv, monthRegistration: regRate, hasInvoicePhones: mr.some((r) => r.phone || r.client) };
  }, [fil, cst]);

  // ─── Chart rendering: 4 visualizations merged from old "Charts" tab ───
  React.useEffect(() => {
    if (!ds.length) return;
    // 1. Daily revenue (last 30 days) — bar with best-day highlight
    const L = ds.slice(-30);
    const bestT = L.reduce((m, d) => Math.max(m, d.total), 0);
    mkC("ov-rev", "bar", L.map((d) => d.date.slice(5)), [{
      label: "الإيراد المدفوع",
      data: L.map((d) => d.total),
      backgroundColor: L.map((d) => (d.total === bestT ? "rgba(16,185,129,0.85)" : "rgba(194,169,127,0.75)")),
      borderRadius: 4,
    }]);

    // 2. Revenue + Discount + Profit stacked-ish (multi-series bar) — last 30 days
    //    v200.51 — Profit uses the period-derived `paidCogsPct`. If the
    //    ratio isn't available yet (initial load / no priced lines in
    //    window), the profit series is dropped rather than showing a
    //    stale/hardcoded number.
    const paidRatio = hasCogs ? paidCogsPct : null;
    const stackSeries = [
      { label: "المدفوع", data: L.map((d) => d.total), backgroundColor: "rgba(107,78,61,0.8)", borderRadius: 3 },
      { label: "الخصم", data: L.map((d) => d.disc), backgroundColor: "rgba(217,119,6,0.7)", borderRadius: 3 },
    ];
    if (paidRatio !== null) {
      stackSeries.push({
        label: "الربح (قبل التكاليف الثابتة)",
        data: L.map((d) => Math.max(0, d.total * (1 - paidRatio))),
        backgroundColor: "rgba(16,185,129,0.75)",
        borderRadius: 3,
      });
    }
    mkC("ov-stack", "bar", L.map((d) => d.date.slice(5)), stackSeries);

    // 3. Weekly bar chart (highlights peak week)
    if (wks.length) {
      const bestW = Math.max(...wks.map((w) => w.total));
      mkC("ov-wk", "bar", wks.map((w) => w.week.slice(5)), [{
        label: "الإيراد الأسبوعي",
        data: wks.map((w) => w.total),
        backgroundColor: wks.map((w) => (w.total === bestW ? "rgba(16,185,129,0.85)" : "rgba(194,169,127,0.75)")),
        borderRadius: 5,
      }]);
    }

    // 4. Margin distribution doughnut (categorical) — uses the
    //    period-derived paid COGS. If not available, the doughnut is
    //    cleared rather than rendered against a stale constant.
    if (!hasCogs) {
      mkC("ov-margin", "doughnut", ["الهامش قيد الحساب"], [{
        data: [1],
        backgroundColor: ["rgba(107,114,128,0.3)"],
        borderWidth: 2,
        borderColor: "#fff",
      }]);
    } else {
      const mgs = ds.map((d) => (d.total > 0 ? ((d.total * (1 - paidCogsPct)) / d.total) * 100 : 0));
      mkC("ov-margin", "doughnut", ["خسارة", "0-5%", "5-10%", "10-20%", "20%+"], [{
        data: [
          mgs.filter((m) => m < 0).length,
          mgs.filter((m) => m >= 0 && m < 5).length,
          mgs.filter((m) => m >= 5 && m < 10).length,
          mgs.filter((m) => m >= 10 && m < 20).length,
          mgs.filter((m) => m >= 20).length,
        ],
        backgroundColor: ["#DC2626", "#D97706", "#C2A97F", "#10B981", "#1E6390"],
        borderWidth: 2,
        borderColor: "#fff",
      }]);
    }
  }, [ds, wks, paidCogsPct, hasCogs]);

  if (!ds.length) return <div className="card" data-testid="ov-empty">لا توجد بيانات — ارفع ملف فواتير من تبويب الرفع.</div>;

  // ── KPI list (smart, focused — pure operations/marketing, no 59) ──
  // v200.51 (2026-08-14) — TWO separate profit-related KPIs:
  //   #1 «الربح قبل التكاليف الثابتة» — from paidCogsPct (period-derived).
  //   #2 «كلفة الأصناف المجانية» — free.priced_cost (campaign giveaway
  //       cost, kept explicitly separate from margin, per owner rule).
  //   Both display coverage-by-revenue AND coverage-by-quantity in
  //   their subtitle. `null` = «قيد الحساب» — no fallback to a stale
  //   constant. FIX (fixed monthly costs) is NOT deducted here because
  //   it has no data source; see the banner below for the honest tag.
  const covLabel = (byRev, byQty) => {
    const rv = byRev !== null ? `${(byRev * 100).toFixed(0)}%` : "—";
    const qt = byQty !== null ? `${(byQty * 100).toFixed(0)}%` : "—";
    return `تغطية: إيراد ${rv} · كمية ${qt}`;
  };
  const paidProfitTile = hasCogs
    ? {
        l: "📦 الربح قبل التكاليف الثابتة",
        v: fm(Math.round(pf)) + " ر",
        s: `هامش ${mg.toFixed(1)}% · ${covLabel(covByRev, covByQty)}`,
        c: mg >= 16 ? "g" : mg >= 8 ? "" : "r",
      }
    : {
        l: "📦 الربح قبل التكاليف الثابتة",
        v: cogsErr ? "غير متاح" : "قيد الحساب",
        s: cogsErr
          ? "الخادم رفض القراءة أو النافذة بلا سطور مُسعّرة"
          : "جارٍ اشتقاق نسبة COGS من product_costs × pos_sales لهذه النافذة",
        c: "",
      };
  const freeGoodsTile = {
    l: "🎁 كلفة الأصناف المجانية",
    v: freeGoodsCost !== null ? fm(Math.round(freeGoodsCost)) + " ر" : (cogsErr ? "غير متاح" : "قيد الحساب"),
    s: freeGoodsCost !== null
      ? `أُهديت خلال النافذة · ${covLabel(covByRev, covByQty)}`
      : "قيمة الأصناف الممنوحة مجاناً (خصم كامل/ضيافة) — كلفة حملات، ليست هامشاً",
    c: freeGoodsCost !== null && freeGoodsCost > 0 ? "o" : "",
  };
  const kpisInvoice = [
    { l: "💰 الإيراد المدفوع", v: fm(tr) + " ر", s: `قبل الخصم ${fm(gross)} ر`, c: "g" },
    { l: "📉 نسبة الخصم", v: dp.toFixed(1) + "%", s: `${fm(tdisc)} ر`, c: dp > 65 ? "r" : dp > 50 ? "o" : "" },
    paidProfitTile,
    freeGoodsTile,
    { l: "🧾 متوسط الفاتورة", v: aov.toFixed(0) + " ر", s: "من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية", c: aov >= 18 ? "g" : aov >= 14 ? "" : "o" },
    { l: "📅 متوسط يومي", v: fm(ar) + " ر", s: `على مدى ${days} يوم`, c: "" },
    { l: "✅ نسبة المدفوع", v: paidRate.toFixed(0) + "%", s: `${fm(tp)} من ${fm(tc)} فاتورة`, c: paidRate >= 80 ? "g" : paidRate >= 60 ? "" : "o" },
    { l: "🏆 أفضل يوم", v: fm(best.total) + " ر", s: best.date, c: "g" },
    { l: "⚠ أضعف يوم", v: fm(worst.total) + " ر", s: worst.date, c: "r" },
  ];

  const kpisPos = hasDsum ? [
    { l: "💰 الإيراد (POS)", v: fm(dsumAgg.net_with_tax) + " ر", s: `${dsumAgg.days} يوم · شامل ضريبة`, c: "g" },
    { l: "📦 صافي بدون ضريبة", v: fm(dsumAgg.net) + " ر", s: "بعد الإرجاع/الإلغاء", c: "b" },
    { l: "📉 إجمالي الخصم", v: fm(dsumAgg.disc) + " ر", s: dsumAgg.gross > 0 ? (dsumAgg.disc / dsumAgg.gross * 100).toFixed(1) + "%" : "—", c: "o" },
    { l: "📄 عدد الفواتير", v: fm(dsumAgg.cnt), s: `متوسط ${dsumAgg.avg.toFixed(0)} ر — من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية`, c: "" },
    { l: "💵 الربح (POS)", v: fm(dsumAgg.profit) + " ر", s: dsumAgg.net_with_tax > 0 ? (dsumAgg.profit / dsumAgg.net_with_tax * 100).toFixed(1) + "% هامش" : "—", c: dsumAgg.profit >= 0 ? "g" : "r" },
    { l: "👥 متوسط زبائن/يوم", v: fm(Math.round(dsumAgg.customers / Math.max(dsumAgg.days, 1))), s: `إجمالي ${fm(dsumAgg.customers)} زيارة`, c: "" },
    { l: "↩ خسائر التشغيل", v: fm(dsumAgg.return_value + dsumAgg.cancel_value) + " ر", s: `${fm(dsumAgg.return_count + dsumAgg.cancel_count)} إرجاع/إلغاء`, c: (dsumAgg.return_value + dsumAgg.cancel_value) > 0 ? "o" : "" },
    { l: "🏆 أفضل يوم", v: fm(best.total) + " ر", s: best.date, c: "g" },
  ] : null;

  return (
    <div data-testid="overview-page">
      {/* ─── FRONTEND-COMPUTED BANNER (v200.51 — honest source labelling) ─── */}
      {/* Every scalar on this screen is computed in the browser from raw
          invoice rows. The paid-COGS ratio and free-goods cost are now
          fetched per-period from `/api/analytics/cogs-window`. Fixed
          monthly costs remain unsourced and are labelled honestly. */}
      <div data-testid="dashboard-frontend-computed-banner"
           style={{
             fontSize: 11, padding: "6px 10px", marginBottom: 10,
             background: "rgba(107,114,128,0.08)",
             border: "1px solid rgba(107,114,128,0.25)",
             borderRadius: 6, color: "#4B5563",
           }}>
        محسوب في المتصفح — الإيراد/الخصم/AOV/MTD تُبنى من صفوف الفواتير الخام.
        {" "}
        <b>نسبة COGS للأصناف المدفوعة</b> و<b>كلفة الأصناف المجانية</b>
        {" "}
        تُشتقّان لهذه النافذة تحديداً من
        {" "}<code>/api/analytics/cogs-window</code>{" "}
        (تكلفة المنتج × الكميات الفعلية من pos_sales).
        <span data-testid="dashboard-fix-unsourced-tag"
              style={{
                marginInlineStart: 6, padding: "1px 6px",
                borderRadius: 4, background: "rgba(220,38,38,0.10)",
                border: "1px solid rgba(220,38,38,0.35)", color: "#B91C1C",
                fontWeight: 700,
              }}>
          التكاليف الثابتة — غير موثّقة
        </span>
        {" "}لا مصدر بيانات — لا تُخصم من الربح حتى يُدخِلها المالك في مجموعة مخصّصة.
      </div>

      {/* ─── COMPANY HEALTH STRIP (cross-page KPIs visible to every employee) ─── */}
      {!hideHealth && <HealthKpiBar />}

      {/* ─── DAY-OVER-DAY QUICK COMPARE ─── */}
      <DayCompareStrip ds={ds} />

      {/* ─── HERO ─── */}
      <div data-testid="ov-hero" style={{
        marginBottom: 14, padding: "18px 22px", borderRadius: 14,
        background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 100%)", color: "#fff",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 1 }}>نظرة عامة</div>
            <h2 style={{ margin: "4px 0 2px", fontSize: 24, fontWeight: 800 }}>
              📊 لوحة الأداء — {days} يوم
            </h2>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              {fm(tc)} فاتورة · {fm(tp)} مدفوعة · {fm(monthCount)} فاتورة هذا الشهر
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <HeroTile label="الإيراد" value={fm(tr) + " ر"} />
            <HeroTile label="متوسط/يوم" value={fm(ar) + " ر"} />
            <HeroTile
              label="الهامش (قبل التكاليف الثابتة)"
              value={hasCogs ? mg.toFixed(1) + "%" : "قيد الحساب"}
              highlight
              tone={hasCogs ? (mg < 0 ? "danger" : mg < 8 ? "warn" : "good") : undefined}
            />
            <HeroTile label="آخر يوم" value={fm(ds[ds.length - 1]?.total || 0) + " ر"} sub={ds[ds.length - 1]?.date} />
          </div>
        </div>
      </div>

      {/* ─── GOAL TRACKING ─── */}
      <GoalsBar
        monthRevenue={monthRevenue}
        monthRegistration={monthRegistration}
        monthAvgInvoice={monthAvgInvoice}
        monthLabel={(new Date()).toISOString().slice(0, 7)}
        hasInvoicePhones={hasInvoicePhones}
      />

      {/* ─── SMART ALERTS ─── */}
      {alerts.length > 0 && (
        <div data-testid="ov-alerts" style={{ marginBottom: 14 }}>
          {alerts.map((a) => <div key={`${a.t}-${a.m.slice(0, 40)}`} className={`al al${a.t}`}>{a.m}</div>)}
        </div>
      )}

      {/* ─── RECONCILIATION BANNER (only if dsum) ─── */}
      {hasDsum && (() => {
        const invPaid = tr;
        const dsPaid = dsumAgg.net_with_tax;
        const paidGap = dsPaid - invPaid;
        const paidPct = invPaid > 0 ? Math.abs(paidGap / invPaid * 100) : 0;
        const significant = paidPct >= 1;
        return (
          <div className={`al ${significant ? "alo" : "alg"}`} data-testid="reconcile-banner" style={{ fontSize: 12.5, lineHeight: 1.9, marginBottom: 14 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div>
                <b>📅 المصدر الرسمي: تقرير POS</b> ({dsumAgg.days} يوم){" "}
                {significant ? (
                  <>— فجوة {paidGap > 0 ? "+" : ""}{fm(Math.round(paidGap))} ر ({paidPct.toFixed(1)}%) مقابل ملف الفواتير.</>
                ) : <>— التطابق ممتاز ✅ (&lt; 1%).</>}
              </div>
              {significant && (
                <button type="button" className="btn btn-success" onClick={runAi} disabled={aiBusy} data-testid="ai-reconcile-run" style={{ fontSize: 12 }}>
                  {aiBusy ? "⏳ تحليل..." : aiText ? "🔄 إعادة" : "🤖 حلّل بالذكاء الاصطناعي"}
                </button>
              )}
            </div>
            {aiErr && <div className="al alr" data-testid="ai-reconcile-error" style={{ marginTop: 8, fontSize: 12 }}>⚠ {aiErr}</div>}
            {aiText && <div data-testid="ai-reconcile-output" style={{ marginTop: 10, padding: 12, background: "var(--card)", borderRadius: 8, border: "1px solid var(--border)", whiteSpace: "pre-wrap", lineHeight: 1.85, fontSize: 13, color: "var(--brown-deep)" }}>{aiText}</div>}
          </div>
        );
      })()}

      {/* ─── KPI GRID ─── */}
      <Kpi items={hasDsum ? kpisPos : kpisInvoice} />

      {/* ─── CHART STRIP (merged from old Charts tab) ─── */}
      <div className="st" style={{ marginTop: 18 }}>📊 الرسوم البيانية</div>
      <div className="g2">
        <ChartCard title="📈 الإيراد اليومي (آخر 30 يوم)" id="ov-rev" testid="chart-rev" />
        <ChartCard title="💰 المدفوع · الخصم · الربح" id="ov-stack" testid="chart-stack" />
      </div>
      <div className="g2">
        <ChartCard title="📆 الإيراد الأسبوعي" id="ov-wk" testid="chart-wk" />
        <ChartCard title="🎯 توزيع أيام الهامش" id="ov-margin" testid="chart-margin" small />
      </div>

      {/* ─── LAST 14 DAYS TABLE ─── */}
      <div className="st" data-testid="last14-title" style={{ marginTop: 18 }}>
        📋 آخر 14 يوم
        {hasDsum && <span style={{ fontSize: 11, color: "var(--success)", marginInlineStart: 8, fontWeight: 600 }}>📅 من POS</span>}
      </div>
      <div className="tw">
        <table>
          <thead><tr><th>التاريخ</th><th>الإيراد الكلي</th><th>المدفوع</th><th>الخصم%</th><th>الربح</th><th>الهامش</th><th>الفواتير</th><th>متوسط الفاتورة (من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية)</th><th>اتجاه</th></tr></thead>
          <tbody>
            {hasDsum ? (() => {
              const sorted = [...dsumFiltered].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 14);
              return sorted.map((r, i) => {
                const dp2 = r.gross > 0 ? (r.disc / r.gross) * 100 : 0;
                const mg2 = r.net_with_tax > 0 ? (r.profit / r.net_with_tax) * 100 : 0;
                const dc = dp2 > 65 ? "dn" : dp2 < 50 ? "up" : "";
                const mc = mg2 >= 20 ? "up" : mg2 < 0 ? "dn" : "";
                const aov2 = r.count > 0 ? r.net_with_tax / r.count : 0;
                const prev = sorted[i + 1];
                let trend = null;
                if (prev) {
                  const df = r.net_with_tax - prev.net_with_tax;
                  trend = df > 0 ? <span className="up">▲</span> : df < 0 ? <span className="dn">▼</span> : <span className="eq">←</span>;
                }
                return (
                  <tr key={r.date} data-testid={`last14-row-${r.date}`}>
                    <td><b>{r.date}</b></td>
                    <td>{fm(Math.round(r.gross))} ر</td>
                    <td>{fm(Math.round(r.net_with_tax))} ر</td>
                    <td className={dc}>{dp2.toFixed(1)}%</td>
                    <td className={mc}>{fm(Math.round(r.profit))} ر</td>
                    <td className={mc}>{mg2.toFixed(1)}%</td>
                    <td>{fm(r.count)}</td>
                    <td>{aov2.toFixed(0)} ر</td>
                    <td>{trend}</td>
                  </tr>
                );
              });
            })() : last14.map((d, i) => {
              const gr2 = d.total + d.disc;
              const dp2 = gr2 > 0 ? d.disc / gr2 * 100 : 0;
              // v200.51 — Per-row profit uses the period-derived
              // `paidCogsPct`. `null` = ratio unavailable → show «—».
              const pf2 = hasCogs ? d.total * (1 - paidCogsPct) : null;
              const mg2 = (hasCogs && d.total > 0) ? pf2 / d.total * 100 : null;
              const aov2 = d.paid > 0 ? d.total / d.paid : 0;
              const dc = dp2 > 65 ? "dn" : dp2 < 50 ? "up" : "";
              const mc = (mg2 !== null && mg2 >= 20) ? "up" : (mg2 !== null && mg2 < 0) ? "dn" : "";
              const prev = last14[i + 1];
              let trend = null;
              if (prev) {
                const df = d.total - prev.total;
                trend = df > 0 ? <span className="up">▲</span> : df < 0 ? <span className="dn">▼</span> : <span className="eq">←</span>;
              }
              return (
                <tr key={d.date} data-testid={`last14-row-${d.date}`}>
                  <td><b>{d.date}</b></td>
                  <td>{fm(gr2)} ر</td>
                  <td>{fm(d.total)} ر</td>
                  <td className={dc}>{dp2.toFixed(1)}%</td>
                  <td className={mc}>{pf2 !== null ? fm(Math.round(pf2)) + " ر" : "—"}</td>
                  <td className={mc}>{mg2 !== null ? mg2.toFixed(1) + "%" : "—"}</td>
                  <td>{fm(d.count)}</td>
                  <td>{aov2.toFixed(0)} ر</td>
                  <td>{trend}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompareCell({ label, t, y, fmFn = fm, suffix = "", inverse = false }) {
  const diff = t - y;
  const pct = y !== 0 ? (diff / Math.abs(y)) * 100 : (t !== 0 ? 100 : 0);
  const isGood = inverse ? diff <= 0 : diff >= 0;
  const arrow = diff === 0 ? "→" : diff > 0 ? "▲" : "▼";
  const color = diff === 0 ? "var(--muted)" : isGood ? "#10B981" : "#EF4444";
  return (
    <div style={{
      flex: 1, minWidth: 140, padding: "10px 14px", background: "var(--card)",
      borderRadius: 8, border: "1px solid var(--border)", textAlign: "center",
    }}>
      <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600, letterSpacing: 0.5 }}>{label}</div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 6, marginTop: 4 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: "var(--brown-deep)" }}>
          {fmFn(t)}{suffix}
        </span>
        <span style={{ fontSize: 11, color: "var(--muted)" }}>vs {fmFn(y)}{suffix}</span>
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color, marginTop: 3 }}>
        {arrow} {Math.abs(pct).toFixed(1)}%
      </div>
    </div>
  );
}

function DayCompareStrip({ ds }) {
  const [mode, setMode] = React.useState("yesterday"); // "yesterday" | "sameDow"
  if (!ds || ds.length < 2) return null;

  const today = ds[ds.length - 1];
  let compare = null;
  let compareLabel = "أمس";

  if (mode === "yesterday") {
    compare = ds[ds.length - 2];
  } else {
    // Find the date exactly 7 days before today.date — parse "YYYY-MM-DD" as LOCAL (not UTC)
    const target = ksaAddDays(String(today.date).slice(0, 10), -7);
    compare = ds.find((row) => row.date === target) || null;
    const arDow = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
    compareLabel = `${arDow[ksaDayOfWeek(target)]} الماضي`;
  }

  if (!compare) {
    return (
      <div data-testid="day-compare-strip" style={{
        marginBottom: 14, padding: "12px 14px", borderRadius: 12,
        background: "var(--beige)", border: "1px solid var(--border)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)" }}>
            ⚡ مقارنة سريعة — لا توجد بيانات للمقارنة
          </div>
          <CompareModeToggle mode={mode} setMode={setMode} />
        </div>
      </div>
    );
  }

  const todayAov = today.paid > 0 ? today.total / today.paid : 0;
  const compAov  = compare.paid > 0 ? compare.total / compare.paid : 0;

  return (
    <div data-testid="day-compare-strip" style={{
      marginBottom: 14, padding: "12px 14px", borderRadius: 12,
      background: "var(--beige)", border: "1px solid var(--border)",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 10, flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)" }}>
          ⚡ نظرة سريعة — اليوم ({today.date}) مقابل {compareLabel} ({compare.date})
        </div>
        <CompareModeToggle mode={mode} setMode={setMode} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <CompareCell label="💰 الإيراد"        t={today.total} y={compare.total} suffix=" ر" />
        <CompareCell label="📄 الفواتير"        t={today.count} y={compare.count} />
        <CompareCell label="✅ المدفوع"         t={today.paid}  y={compare.paid} />
        <CompareCell label="🧾 متوسط الفاتورة (من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية)" t={todayAov}    y={compAov} fmFn={(v) => Math.round(v)} suffix=" ر" />
      </div>
    </div>
  );
}

function CompareModeToggle({ mode, setMode }) {
  const opts = [
    { id: "yesterday", label: "vs أمس" },
    { id: "sameDow",   label: "vs نفس اليوم الأسبوع الماضي" },
  ];
  return (
    <div style={{ display: "inline-flex", gap: 4, padding: 3, background: "var(--card)", borderRadius: 8, border: "1px solid var(--border)" }} data-testid="compare-mode-toggle">
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => setMode(o.id)}
          data-testid={`compare-mode-${o.id}`}
          style={{
            padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer",
            background: mode === o.id ? "var(--brown)" : "transparent",
            color: mode === o.id ? "#fff" : "var(--brown-deep)",
            fontSize: 11, fontWeight: 700, transition: "all 140ms ease",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function HeroTile({ label, value, sub, highlight, tone }) {
  const toneColors = {
    danger: { bg: "rgba(220,38,38,0.35)", border: "rgba(255,180,180,0.55)" },
    warn:   { bg: "rgba(245,158,11,0.30)", border: "rgba(254,215,170,0.55)" },
    good:   { bg: "rgba(16,185,129,0.30)", border: "rgba(167,243,208,0.55)" },
  };
  const t = tone && toneColors[tone];
  return (
    <div style={{
      background: t ? t.bg : highlight ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.10)",
      padding: "10px 18px", borderRadius: 10, minWidth: 120,
      border: t ? `1.5px solid ${t.border}` : highlight ? "1.5px solid rgba(255,255,255,0.4)" : "none",
    }}>
      <div style={{ fontSize: 10, opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, id, testid, small }) {
  return (
    <div className="card" data-testid={testid}>
      <h4>{title}</h4>
      <div className={small ? "csm" : "cw"}><canvas id={id}></canvas></div>
    </div>
  );
}

// ======================= BONUS (مبني على المبيعات) =======================
export function Bonus({ fil }) {
  const [branch, setBranch] = React.useState({
    baristas: 9,
    managers: 1,
    monthly_target_sar: 200000,
    bonus_pct: 4.0,
    manager_share_pct: 35.0,
  });

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/settings/branch");
        if (alive) setBranch({
          baristas: data.baristas || 9,
          managers: data.managers || 1,
          monthly_target_sar: data.monthly_target_sar ?? 200000,
          bonus_pct: data.bonus_pct ?? 4.0,
          manager_share_pct: data.manager_share_pct ?? 35.0,
        });
      } catch { /* offline → keep defaults */ }
    };
    load();
    const onUpdate = () => load();
    window.addEventListener("branch:updated", onUpdate);
    return () => { alive = false; window.removeEventListener("branch:updated", onUpdate); };
  }, []);

  // Compute total PAID sales for the CURRENT month (Saudi calendar). Falls back to whatever
  // `fil` contains if there's no data in the current month.
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthInvoices = fil.filter((r) => (r.date || "").startsWith(ym) && r.isPaid);
  const monthSales = monthInvoices.reduce((s, r) => s + (Number(r.total) || 0), 0);
  // Also expose previous month for context
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const ymPrev = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}`;
  const prevSales = fil.filter((r) => (r.date || "").startsWith(ymPrev) && r.isPaid)
    .reduce((s, r) => s + (Number(r.total) || 0), 0);

  if (!fil.length) {
    return <div className="card">لا توجد بيانات</div>;
  }

  const targetReached = monthSales >= branch.monthly_target_sar;
  const progressPct = branch.monthly_target_sar > 0
    ? Math.min(100, monthSales / branch.monthly_target_sar * 100)
    : 0;
  // Bonus pool = sales × bonus_pct (only if target reached)
  const bonusPool = targetReached ? (monthSales * branch.bonus_pct / 100) : 0;
  const mgrShare = bonusPool * (branch.manager_share_pct / 100);
  const baristaPool = bonusPool - mgrShare;
  const perBarista = branch.baristas > 0 ? baristaPool / branch.baristas : 0;
  const perManager = branch.managers > 0 ? mgrShare / branch.managers : 0;

  // Gap-to-target & projected (linear) for transparency
  const dayOfMonth = ksaParts(now).date;
  const daysInMonth = ksaDaysInMonth(ksaToday(now));
  const projectedMonth = dayOfMonth > 0 ? monthSales / dayOfMonth * daysInMonth : 0;
  const gap = Math.max(0, branch.monthly_target_sar - monthSales);
  const dailyAvgNeeded = (daysInMonth - dayOfMonth) > 0 ? gap / (daysInMonth - dayOfMonth) : gap;

  return (
    <div data-testid="bonus-page">
      {/* Hero status banner */}
      <div className="card" style={{
        marginBottom: 14,
        padding: "16px 20px",
        background: targetReached
          ? "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(194,169,127,0.08) 100%)"
          : "linear-gradient(135deg, rgba(217,119,6,0.08) 0%, rgba(107,78,61,0.05) 100%)",
        borderInlineStart: `4px solid ${targetReached ? "var(--success)" : "var(--warn)"}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1 }}>بونس شهر {ym}</div>
            <h3 style={{ margin: "4px 0 2px", color: "var(--brown-deep)", fontSize: 22, fontWeight: 800 }}>
              {targetReached
                ? `🎉 الهدف تحقق! بونس الشهر: ${fm(Math.round(bonusPool))} ر`
                : `🎯 المتبقي للهدف: ${fm(Math.round(gap))} ر`}
            </h3>
            <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
              {targetReached
                ? `${branch.bonus_pct}% من المبيعات الشهرية (${fm(Math.round(monthSales))} ر)`
                : `بحاجة ${fm(Math.round(dailyAvgNeeded))} ر/يوم في الـ${daysInMonth - dayOfMonth} يوم المتبقية`}
            </div>
          </div>
          <div style={{ textAlign: "end" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: targetReached ? "var(--success)" : "var(--warn)" }}>
              {progressPct.toFixed(0)}%
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>من الهدف الشهري</div>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ marginTop: 12, height: 10, background: "var(--beige)", borderRadius: 8, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: progressPct + "%",
            background: targetReached
              ? "linear-gradient(90deg, var(--success) 0%, #14B8A6 100%)"
              : "linear-gradient(90deg, var(--warn) 0%, var(--gold) 100%)",
            transition: "width 600ms ease",
          }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "var(--muted)", marginTop: 4 }}>
          <span>0</span>
          <span>المتوقع نهاية الشهر: <b style={{ color: "var(--brown-deep)" }}>{fm(Math.round(projectedMonth))} ر</b></span>
          <span>الهدف {fm(branch.monthly_target_sar)} ر</span>
        </div>
      </div>

      {/* Staff + formula row */}
      <div className="card" style={{
        marginBottom: 14,
        padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        background: "var(--beige)",
      }} data-testid="branch-staff-row">
        <span style={{ fontSize: 13, color: "var(--brown)" }}>
          👥 <b>{branch.managers}</b> مدير · <b>{branch.baristas}</b> باريستا
        </span>
        <span style={{ fontSize: 13, color: "var(--brown)" }}>
          📐 <b>{branch.bonus_pct}%</b> من المبيعات عند تحقيق <b>{fm(branch.monthly_target_sar)} ر</b>
        </span>
        <span style={{ fontSize: 13, color: "var(--brown)" }}>
          🧮 <b>{branch.manager_share_pct}%</b> للمدير · <b>{(100 - branch.manager_share_pct).toFixed(0)}%</b> يقسم على الباريستات
        </span>
        <span style={{ fontSize: 11, color: "var(--muted)", marginInlineStart: "auto" }}>
          الإعدادات → عدد الموظفين + معادلة البونس
        </span>
      </div>

      {/* KPIs */}
      <Kpi items={[
        { l: "💰 مبيعات الشهر", v: fm(Math.round(monthSales)) + " ر", s: `الشهر الماضي ${fm(Math.round(prevSales))} ر`, c: targetReached ? "g" : "o" },
        { l: "🎯 الهدف الشهري", v: fm(branch.monthly_target_sar) + " ر", s: `${branch.bonus_pct}% بونس عند الوصول`, c: "" },
        { l: "📦 إجمالي البونس", v: fm(Math.round(bonusPool)) + " ر", s: targetReached ? "✅ تم تحقيق الهدف" : "في انتظار تحقيق الهدف", c: targetReached ? "g" : "" },
        { l: "👑 بونس المدير", v: fm(Math.round(perManager)) + " ر", s: `${branch.manager_share_pct}% من البونس`, c: targetReached ? "g" : "" },
        { l: "☕ بونس كل باريستا", v: fm(Math.round(perBarista)) + " ر", s: `${(100 - branch.manager_share_pct).toFixed(0)}% ÷ ${branch.baristas}`, c: targetReached ? "g" : "" },
      ]} />

      {/* What-if scenarios */}
      <div style={{ marginTop: 14 }}>
        <div className="st">📊 ماذا لو حققنا أكثر؟</div>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 13 }}>
            <thead style={{ background: "var(--beige)" }}>
              <tr>
                <th style={{ padding: 10, textAlign: "start" }}>سيناريو</th>
                <th style={{ padding: 10 }}>المبيعات</th>
                <th style={{ padding: 10 }}>إجمالي البونس</th>
                <th style={{ padding: 10 }}>بونس المدير</th>
                <th style={{ padding: 10 }}>بونس كل باريستا</th>
              </tr>
            </thead>
            <tbody>
              {[
                { l: "الهدف بالضبط", s: branch.monthly_target_sar, hl: false },
                { l: "+10% من الهدف", s: branch.monthly_target_sar * 1.1, hl: false },
                { l: "+25% من الهدف", s: branch.monthly_target_sar * 1.25, hl: false },
                { l: "+50% من الهدف", s: branch.monthly_target_sar * 1.5, hl: false },
                { l: "الحالي", s: monthSales, hl: true },
              ].map((row) => {
                const pool = row.s >= branch.monthly_target_sar ? (row.s * branch.bonus_pct / 100) : 0;
                const mgr = pool * (branch.manager_share_pct / 100);
                const baristaTotal = pool - mgr;
                const perB = branch.baristas > 0 ? baristaTotal / branch.baristas : 0;
                const perM = branch.managers > 0 ? mgr / branch.managers : 0;
                return (
                  <tr key={row.l} style={{
                    background: row.hl ? "rgba(194,169,127,0.12)" : "transparent",
                    fontWeight: row.hl ? 700 : 400,
                    borderTop: "1px solid var(--border)",
                  }}>
                    <td style={{ padding: 10 }}>{row.hl ? "🔵 " : ""}{row.l}</td>
                    <td style={{ padding: 10, textAlign: "center" }}>{fm(Math.round(row.s))} ر</td>
                    <td style={{ padding: 10, textAlign: "center", color: pool > 0 ? "var(--success)" : "var(--muted)" }}>
                      {pool > 0 ? fm(Math.round(pool)) + " ر" : "—"}
                    </td>
                    <td style={{ padding: 10, textAlign: "center" }}>{pool > 0 ? fm(Math.round(perM)) + " ر" : "—"}</td>
                    <td style={{ padding: 10, textAlign: "center" }}>{pool > 0 ? fm(Math.round(perB)) + " ر" : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8, lineHeight: 1.8 }}>
          💡 <b>منطق الحساب:</b> لو المبيعات ≥ الهدف → بونس = المبيعات × النسبة. أقل من الهدف = صفر بونس.
          البونس يتناسب طردياً مع المبيعات (كلما زادت المبيعات زاد البونس). محفّز بدون مخاطرة على التشغيل.
        </div>
      </div>
    </div>
  );
}
